import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnInit, Output, SimpleChange, SimpleChanges, ViewChild, ElementRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { AuthConfig, AuthService, AuthUser, FCMNotificationActionEnum, proBySourceEnum, ResponseObject } from "src/app/Services/auth.service";
import { ChatboxService } from "src/app/Services/chatbox.service";
import { MessagingService } from "src/app/Services/messaging.service";
import { NotificationsService } from "src/app/Services/shared/notifications.service";
import { SharedUtilityService } from "src/app/Services/shared/shared.utility.service";
import { ChatBoxEventsEnums, NotificationTypes } from "src/app/app.enums";
import { ChatUser, Messages } from "src/app/models/chatBox.model";
import { ChatMessageReceivedSignalR, SignalRService } from "src/app/signalr.service";
import { TranslocoService } from "@jsverse/transloco";

@Component({
    selector: 'chat-messages',
    templateUrl: './messages.component.html',
    styleUrls: ['./messages.component.css'],
    inputs: ['chatUsers', 'selectedUser', 'onlineUsers'],
    outputs: ['onNewMessageReceived'],
    providers: [AuthService, SharedUtilityService],
    standalone: false
})
export class AppMessagesComponent implements OnInit {
    public authConfig!: AuthConfig;
    public chatUsers: ChatUser[] = [];
    public selectedUser!: ChatUser;
    public onlineUsers: string[] = [];
    public onNewMessageReceived = new EventEmitter<any>();
    public loggedInUserId!: string;
    public loggedInUser!: AuthUser | null;
    public selectedUserId!: string;
    public selectedCurrentUser!: ChatUser;
    public sendMessageData: Messages = {} as Messages;
    public messages: Messages[] = [];
    public data: any[] = [];
    public messagesLoaded: boolean = false;
    public displayEmojiDialog = false;
    public ChatBoxEventsEnums!: ChatBoxEventsEnums;
    public proBySourceEnum: any = proBySourceEnum;

    @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

    constructor(private activatedRoute: ActivatedRoute,
        private ngxSpinnerService: NgxSpinnerService,
        private authService: AuthService,
        private chatService: ChatboxService,
        private notificationsService: NotificationsService,
        private router: Router,
        private messagingService: MessagingService,
        private cdr: ChangeDetectorRef,
        private signalRService: SignalRService,
        private transloco: TranslocoService,
        private ngZone: NgZone) { }

    public ngOnInit(): void {
        this.authConfig = this.authService.getAuthConfig();
        this.loggedInUserId = this.authService.getUserId();
        this.loggedInUser = this.authService.getCurrentUser();

        this.signalRService.attachChatMessage().subscribe((newMessage: ChatMessageReceivedSignalR) => {
            this.handleNewMessage(newMessage);
        });

        this.messagingService.currentMessage.subscribe((data: any) => {
            this.handleFCMMessage(data);
        });

        // Subscribe to receive messages
        this.messagingService.receiveMessage();
        this.messagingService.handleMarkasReadBychat(this.loggedInUserId);

        // Listen for messages from the service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
            this.handleFCMMessage(event.data);
        });
    }



    private handleNewMessage(newMessage: ChatMessageReceivedSignalR): void {
        const userId = this.authService.getUserId();
        if ((userId === newMessage.reciverId || userId === newMessage.senderId) && newMessage.messageId) {
            this.chatService.getMessageById(newMessage.messageId).subscribe((message: Messages) => {
                if (!this.messages.some(m => m.Id === message.Id)) {
                    this.ngZone.run(() => {
                        this.messages.push(message);
                        this.onNewMessageReceived.emit(message);
                        this.cdr.detectChanges();
                    });
                }
            }, error => {
                console.error(error);
            });
        }
    }

    private handleFCMMessage(data: any): void {
        if (data && data.data) {
            const { actionType, entityId } = data.data;
            const { title, body, image } = data.notification || {};
            if (actionType === FCMNotificationActionEnum.ChatBoxLive  && entityId) {
                this.chatService.getMessageById(entityId).subscribe(
                    (message: Messages | null) => {
                        if (!message) return;
                        if (!this.messages.some(m => m.Id === message.Id)) {
                            this.ngZone.run(() => {
                                message.SenderName = title || 'Unknown';
                                message.TextMessage = body || 'No message content';
                                message.SenderImgUrl = image || undefined;
                                this.messages.push(message);
                                this.onNewMessageReceived.emit(message);
                                this.cdr.detectChanges();
                            });
                        }
                    },
                    error => {
                        console.error('Error fetching message by ID:', error);
                    }
                );
            }
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const selectedUserChange: SimpleChange = changes['selectedUser'];
        if (selectedUserChange && selectedUserChange.currentValue) {
            this.selectedUserId = selectedUserChange.currentValue.Id;
            this.selectedUser = selectedUserChange.currentValue;
            this.processChats();
            this.cdr.detectChanges();
        }
    }

    private processChats(): void {
        const receiver = this.chatUsers.find(p => p.Id === this.selectedUserId);
        const userId = this.authService.getUserId();
        if (receiver) {
            this.selectedCurrentUser = receiver;
            const fromId = !receiver.IsSenderAnArtist ? userId : this.selectedUserId;
            const toId = !receiver.IsSenderAnArtist ? this.selectedUserId : userId;
            this.getAllMessages(fromId, toId);
        }
    }

    public getAllMessages(galleryId: string, artistId: string): void {
        this.chatService.getAllMessages(galleryId, artistId).subscribe((messages: Messages[]) => {
            this.messages = messages;
            this.messagesLoaded = true;
        }, (error) => {
            console.error(error);
            this.ngxSpinnerService.hide();
            this.notificationsService.showNotification(this.transloco.translate('messages.error'), this.transloco.translate('messages.failedToFetchMessages'), NotificationTypes.Error);
        });
        this.markasreadchatboxNotificaion();
    }

    markasreadchatboxNotificaion(): void {
        this.messagingService.markasreadchatboxNotificaion(this.selectedUserId).subscribe(
            (data) => {
                // Notification marked as read
            },
            (error) => {
                console.error('Error fetching notifications:', error);
            }
        );
    }

    public sendMessage(): void {
        if (!this.sendMessageData.TextMessage) return;
        if (this.authConfig.IsGallery) {
            this.sendMessageData.GalleryId = this.authService.getUserId();
            this.sendMessageData.ArtistId = this.selectedUserId;
        } else if (this.authConfig.IsArtist) {
            this.sendMessageData.GalleryId = this.selectedUserId;
            this.sendMessageData.ArtistId = this.authService.getUserId();
        }
        this.sendMessageData.SenderImgUrl = this.loggedInUser?.PersonalImgUrl;
        this.sendMessageData.SenderName = this.loggedInUser?.FirstName;

        this.chatService.sendMessage(this.sendMessageData).subscribe((sendMessageApiResponse: ResponseObject) => {
            this.ngxSpinnerService.hide();
            if (sendMessageApiResponse.Success) {
                this.sendMessageData = new Messages();
                this.processChats();
                this.chatService.getMessageById(sendMessageApiResponse.ResponseData).subscribe((message: Messages) => {
                    if (!this.messages.some(m => m.Id === message.Id)) {
                        this.ngZone.run(() => {
                            this.messages.push(message);
                            this.onNewMessageReceived.emit(message);
                            this.cdr.detectChanges();
                        });
                    }
                }, error => {
                    console.error(error);
                });
            } else {
                this.notificationsService.showNotification(this.transloco.translate('messages.error'), sendMessageApiResponse.ResponseMessage, NotificationTypes.Error);
            }
        }, (error) => {
            console.error(error);
            this.notificationsService.showNotification(this.transloco.translate('messages.error'), this.transloco.translate('messages.failedToSendMessage'), NotificationTypes.Error);
        });
    }

    public isUserOnline(user: ChatUser): boolean {
        return this.onlineUsers.includes(user.Id);
    }

    public openEmojiDialog() {
        this.displayEmojiDialog = !this.displayEmojiDialog;
    }

    public addEmoji(emoji: string) {
        this.sendMessageData.TextMessage = (this.sendMessageData.TextMessage || '') + emoji;
        this.displayEmojiDialog = false;
    }

    public closeEmojiDialog($event: any) {
        this.displayEmojiDialog = false;
    }

    public ViewEventsDetail(message: Messages): void {
        switch (message.EventTypeId) {
            case ChatBoxEventsEnums.ConsignmentAccepted:
                this.router.navigate(['view-consignments']);
                break;
            case ChatBoxEventsEnums.ConsignmentDispatch:
                if (this.authConfig.IsGallery) {
                    this.router.navigate(['view-consignments']);
                } else {
                    this.router.navigate(['/artists', 'consignments', message.RefrenceId, 'view']);
                }
                break;
            case ChatBoxEventsEnums.Proposal:
                this.router.navigate(['/view-proposals']);
                break;
            case ChatBoxEventsEnums.AcceptProposal:
                this.router.navigate(['view-proposals']);
                break;
            case ChatBoxEventsEnums.RejectProposal:
                this.router.navigate(['/view-proposals']);
                break;
            case ChatBoxEventsEnums.ProductSold:
                if (this.authConfig.IsArtist) {
                    this.router.navigate(['sold-products', message.RefrenceId]);
                    this.router.navigate(['/', this.loggedInUserId, 'artworks', message.RefrenceId]);
                } else {
                    this.router.navigate(['sold-products', message.RefrenceId]);
                    this.router.navigate(['/', this.selectedUser.Id, 'artworks', message.RefrenceId]);
                }
                break;
            case ChatBoxEventsEnums.ProductReturn:
                this.router.navigate(['/', this.loggedInUserId, 'artworks', message.RefrenceId]);
                break;
            default:
                console.error('Unknown EventTypeId:', message.EventTypeId);
                break;
        }
    }
    
public navigateToViewProfile(selectedUser: ChatUser) {
  if (!selectedUser?.UserName) {
    console.warn('UserName is missing for navigation');
    return;
  }

  // Always use /profile/username — works for both Artist & Gallery
  this.router.navigate(['/profile', selectedUser.UserName]);
}


}